import { defineComponent as _defineComponent } from 'vue'
import type { Genres } from '@/constants/types';
import type { AssertByTypename } from '@/helpers/graphql-model-helper';
import type { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import type { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';
import type { GetNodeTitleDetailsQuery } from '@/pages/graphql/queries/GetNodeTitleDetails.query';

import HeadlinedTitleList from '@/components/HeadlinedTitleList.vue';
import SectionContainer from '@/pages/titleDetailV1/components/SectionContainer.vue';

import { CreditRole, ObjectType, Module } from '@/@types/graphql-types';

import { useI18n } from '@/helpers/composables/useI18n';
import { useTitleDetailInfo } from '@/pages/titleDetailV1/v1/composables/useTitleDetailInfo';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { storeToRefs } from 'pinia';

import { computed } from 'vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'TitleDetailSimilarTitlesV2',
  props: {
    displaySimilarTitles: { type: Boolean, default: true },
    title: null,
    genresByShortName: null,
    minTitles: { default: 1 },
    sponsoredAd: { default: undefined },
    additionalContexts: { default: () => [] },
    impressionTrackingLabel: { default: '' },
    impressionTrackingProperty: { default: '' }
  },
  setup(__props: any) {

const props = __props as { displaySimilarTitles: boolean, title: AssertByTypename<GetNodeTitleDetailsQuery['node'], 'Movie' | 'Show' | 'Season'>, genresByShortName: Genres, minTitles: number, sponsoredAd: SponsoredAdFragment | undefined, additionalContexts: SnowplowContext[], impressionTrackingLabel: string, impressionTrackingProperty: string };



const { t, tc } = useI18n();
const languageStore = storeToRefs(useLanguageStore());
const { title, titleObjectType, credits, directors } = useTitleDetailInfo(() => props.title);

const objectTypeTranslation = {
	[ObjectType.Movie]: 'WEBAPP_MOVIES',
	[ObjectType.Show]: 'WEBAPP_TVSHOWS',
};

const titleModules = computed(() => props.title?.titleModules || []);

const modulesByType = computed(() => {
	return (titleModules.value || []).filter(Boolean).reduce((root, item) => {
		root[item!.template.technicalName] = ((item!.content as any)?.titles || []).map((title: any) => title.node);
		return root;
	}, {} as Record<string, Module[]>);
});

const modulesDirectorAndActor = computed(() => {
	const directorTitles = modulesByType.value.TITLE_POPULAR_DIRECTOR || [];
	const actorTitles = modulesByType.value.TITLE_POPULAR_ACTOR || [];
	const combinedTitles = [...directorTitles, ...actorTitles];
	return Array.from(new Set(combinedTitles));
});

const curatedUpcomingHeadline = computed(() => {
	switch (props.title.__typename) {
		case 'Movie':
			return tc('WEBAPP_UPCOMING_MOVIES_LIST');
		case 'Show':
		case 'Season':
			return tc('WEBAPP_UPCOMING_SHOWS_LIST');
		default:
			return '';
	}
});

const firstTitleGenreShortName = computed(() => {
	return props.title.content.genres?.[0]?.shortName ?? null;
});
const similarUpcomingHeadline = computed(() => {
	if (!firstTitleGenreShortName.value) return t('WEBAPP_UPCOMING_TITLES_LIST');

	const genre = props.genresByShortName[firstTitleGenreShortName.value]?.translation;
	switch (props.title.__typename) {
		case 'Movie':
			return t('WEBAPP_UPCOMING_GENRE_MOVIES_LIST', { genre });
		case 'Show':
		case 'Season':
			return t('WEBAPP_UPCOMING_GENRE_SHOWS_LIST', { genre });
		default:
			return '';
	}
});

const director = computed(() => {
	return directors.value.find(Boolean);
});
const mainActor = computed(() => {
	return credits.value.find(credit => credit.role === CreditRole.Actor);
});
const similarActorDirectorHeadline = computed(() => {
	const actorName = mainActor.value?.name;
	const directorName = director.value?.name;
	const titleType = tc(objectTypeTranslation[titleObjectType.value]);
	const from = [actorName, directorName].filter(Boolean).join(' & ');
	const params = { titleType, directorAndActor: from };
	return titleObjectType.value === 'MOVIE'
		? t(`WEBAPP_INTERLINKING_DIRECTOR_ACTOR_MOVIES`, params)
		: t(`WEBAPP_INTERLINKING_DIRECTOR_ACTOR_SHOWS`, params);
});

const similarSubgenres = computed(() => {
	return (
		// if we're on a season page, we need to go over the show.content.subgenres to get them subgenres
		(
			(props.title as AssertByTypename<GetNodeTitleDetailsQuery['node'], 'Season'>).show?.content.subgenres ||
			props.title.content.subgenres ||
			[]
		)
			.map(subgenre => subgenre.content)
			.filter(subgenre => subgenre.shortName)
	);
});
const similarSubgenreHeadline = computed(() => {
	const firstSubgenre = similarSubgenres.value[0] ?? null;
	if (!firstSubgenre) {
		return '';
	}
	return t('WEBAPP_INTERLINKING_SUBGENRE', { subgenre: firstSubgenre.name });
});

const languagesWithAllHeadings = ['en', 'de', 'fr', 'it', 'es', 'pt', 'tr', 'cs', 'pl', 'ar'];
const hasHeadlineTranslation = computed(() => {
	return languagesWithAllHeadings.includes(languageStore.language.value);
});

return { __sfc: true,props, t, tc, languageStore, title, titleObjectType, credits, directors, objectTypeTranslation, titleModules, modulesByType, modulesDirectorAndActor, curatedUpcomingHeadline, firstTitleGenreShortName, similarUpcomingHeadline, director, mainActor, similarActorDirectorHeadline, similarSubgenres, similarSubgenreHeadline, languagesWithAllHeadings, hasHeadlineTranslation, HeadlinedTitleList, SectionContainer }
}

})